import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Typography,
  Box,
  Button,
  Grid,
  FormGroup,
  Container,
  TextareaAutosize,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { initMercadoPago, Wallet } from "@mercadopago/sdk-react";
import { debounce } from "lodash";
import { KeyboardArrowDown, PriorityHigh, Done } from "@mui/icons-material";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import invokeApi from "../../../services/invokeApi";
import logoImage from "../../../assets/images/logo.png";
import logoMercadoPago from "../../../assets/images/mercadopago.png";
import logoWebpay from "../../../assets/images/webpay.png";


initMercadoPago(process.env.REACT_APP_PUBLIC_TOKEN, { locale: "es-CL" });

// Array de países del código de referencia
const COUNTRIES = [
  // Países prioritarios primero
  { code: "+56", name: "Chile", digits: 9 },
  { code: "+54", name: "Argentina", digits: 10 },
  { code: "+1", name: "Estados Unidos", digits: 10 },
  { code: "+34", name: "España", digits: 9 },
  { code: "+46", name: "Suecia", digits: 9 },
  { code: "+1", name: "Canadá", digits: 10 },
  { code: "+61", name: "Australia", digits: 9 },
  { code: "+55", name: "Brasil", digits: 11 },
  { code: "+33", name: "Francia", digits: 10 },
  { code: "+49", name: "Alemania", digits: 11 },
  { code: "+51", name: "Perú", digits: 9 },
  // Resto de países en orden alfabético
  { code: "+93", name: "Afganistán", digits: 9 },
  { code: "+355", name: "Albania", digits: 9 },
  { code: "+213", name: "Argelia", digits: 9 },
  { code: "+376", name: "Andorra", digits: 6 },
  { code: "+244", name: "Angola", digits: 9 },
  { code: "+43", name: "Austria", digits: 10 },
  { code: "+973", name: "Baréin", digits: 8 },
  { code: "+880", name: "Bangladesh", digits: 10 },
  { code: "+32", name: "Bélgica", digits: 9 },
  { code: "+501", name: "Belice", digits: 7 },
  { code: "+591", name: "Bolivia", digits: 8 },
  { code: "+267", name: "Botsuana", digits: 8 },
  { code: "+359", name: "Bulgaria", digits: 9 },
  { code: "+257", name: "Burundi", digits: 8 },
  { code: "+855", name: "Camboya", digits: 9 },
  { code: "+237", name: "Camerún", digits: 9 },
  { code: "+86", name: "China", digits: 11 },
  { code: "+357", name: "Chipre", digits: 8 },
  { code: "+57", name: "Colombia", digits: 10 },
  { code: "+506", name: "Costa Rica", digits: 8 },
  { code: "+385", name: "Croacia", digits: 9 },
  { code: "+53", name: "Cuba", digits: 8 },
  { code: "+45", name: "Dinamarca", digits: 8 },
  { code: "+593", name: "Ecuador", digits: 9 },
  { code: "+20", name: "Egipto", digits: 10 },
  { code: "+503", name: "El Salvador", digits: 8 },
  { code: "+372", name: "Estonia", digits: 8 },
  { code: "+251", name: "Etiopía", digits: 9 },
  { code: "+358", name: "Finlandia", digits: 9 },
  { code: "+233", name: "Ghana", digits: 9 },
  { code: "+30", name: "Grecia", digits: 10 },
  { code: "+502", name: "Guatemala", digits: 8 },
  { code: "+504", name: "Honduras", digits: 8 },
  { code: "+852", name: "Hong Kong", digits: 8 },
  { code: "+36", name: "Hungría", digits: 9 },
  { code: "+91", name: "India", digits: 10 },
  { code: "+62", name: "Indonesia", digits: 10 },
  { code: "+98", name: "Irán", digits: 10 },
  { code: "+964", name: "Irak", digits: 10 },
  { code: "+353", name: "Irlanda", digits: 9 },
  { code: "+354", name: "Islandia", digits: 7 },
  { code: "+972", name: "Israel", digits: 9 },
  { code: "+39", name: "Italia", digits: 10 },
  { code: "+81", name: "Japón", digits: 10 },
  { code: "+962", name: "Jordania", digits: 9 },
  { code: "+7", name: "Kazajistán", digits: 10 },
  { code: "+254", name: "Kenia", digits: 9 },
  { code: "+996", name: "Kirguistán", digits: 9 },
  { code: "+965", name: "Kuwait", digits: 8 },
  { code: "+371", name: "Letonia", digits: 8 },
  { code: "+961", name: "Líbano", digits: 8 },
  { code: "+218", name: "Libia", digits: 9 },
  { code: "+370", name: "Lituania", digits: 8 },
  { code: "+352", name: "Luxemburgo", digits: 9 },
  { code: "+60", name: "Malasia", digits: 9 },
  { code: "+960", name: "Maldivas", digits: 7 },
  { code: "+356", name: "Malta", digits: 8 },
  { code: "+212", name: "Marruecos", digits: 9 },
  { code: "+52", name: "México", digits: 10 },
  { code: "+373", name: "Moldavia", digits: 8 },
  { code: "+377", name: "Mónaco", digits: 8 },
  { code: "+976", name: "Mongolia", digits: 8 },
  { code: "+95", name: "Myanmar", digits: 9 },
  { code: "+977", name: "Nepal", digits: 10 },
  { code: "+505", name: "Nicaragua", digits: 8 },
  { code: "+234", name: "Nigeria", digits: 8 },
  { code: "+47", name: "Noruega", digits: 8 },
  { code: "+64", name: "Nueva Zelanda", digits: 9 },
  { code: "+31", name: "Países Bajos", digits: 9 },
  { code: "+92", name: "Pakistán", digits: 10 },
  { code: "+507", name: "Panamá", digits: 8 },
  { code: "+595", name: "Paraguay", digits: 9 },
  { code: "+63", name: "Filipinas", digits: 10 },
  { code: "+48", name: "Polonia", digits: 9 },
  { code: "+351", name: "Portugal", digits: 9 },
  { code: "+974", name: "Catar", digits: 8 },
  { code: "+44", name: "Reino Unido", digits: 10 },
  { code: "+420", name: "República Checa", digits: 9 },
  { code: "+40", name: "Rumania", digits: 9 },
  { code: "+7", name: "Rusia", digits: 10 },
  { code: "+378", name: "San Marino", digits: 6 },
  { code: "+966", name: "Arabia Saudí", digits: 9 },
  { code: "+65", name: "Singapur", digits: 8 },
  { code: "+421", name: "Eslovaquia", digits: 9 },
  { code: "+386", name: "Eslovenia", digits: 8 },
  { code: "+27", name: "Sudáfrica", digits: 9 },
  { code: "+82", name: "Corea del Sur", digits: 10 },
  { code: "+94", name: "Sri Lanka", digits: 9 },
  { code: "+41", name: "Suiza", digits: 9 },
  { code: "+66", name: "Tailandia", digits: 9 },
  { code: "+90", name: "Turquía", digits: 10 },
  { code: "+380", name: "Ucrania", digits: 9 },
  { code: "+598", name: "Uruguay", digits: 8 },
  { code: "+58", name: "Venezuela", digits: 10 },
  { code: "+84", name: "Vietnam", digits: 9 },
];

// Validaciones inline
// Función para detectar si un valor está anonimizado
const isAnonymizedValue = (value) => {
  return typeof value === 'string' && value.includes('*');
};

const isValidText = (text) => {
  if (isAnonymizedValue(text)) return true;
  return text.length >= 1;
};

// ✅ NUEVA VALIDACIÓN COMPLETA
const validateEmail = (email) => {
  // 1. Verificar que el email existe
  if (!email) {
    return { isValid: false, message: "El correo es obligatorio" };
  }

  // 2. Verificar longitud total (min 5, max 254)
  if (email.length < 5) {
    return { isValid: false, message: "El correo debe tener al menos 5 caracteres" };
  }
  if (email.length > 254) {
    return { isValid: false, message: "El correo no puede tener más de 254 caracteres" };
  }

  // 3. Verificar que no tiene espacios
  if (email.includes(" ")) {
    return { isValid: false, message: "El correo no puede contener espacios" };
  }

  // 4. Verificar solo caracteres ASCII (código 0-127)
  if (!/^[\x00-\x7F]*$/.test(email)) {
    return { isValid: false, message: "El correo no puede contener tildes ni caracteres especiales" };
  }

  // 5. Verificar que tiene exactamente un @
  const atCount = (email.match(/@/g) || []).length;
  if (atCount === 0) {
    return { isValid: false, message: "El correo debe incluir @" };
  }
  if (atCount > 1) {
    return { isValid: false, message: "El correo debe tener solo un @" };
  }

  // 6. Separar parte local y dominio
  const atIndex = email.indexOf("@");
  const localPart = email.substring(0, atIndex);
  const domain = email.substring(atIndex + 1);

  // 7. Validar parte local (antes del @)
  if (localPart.length === 0) {
    return { isValid: false, message: "Debe haber texto antes del @" };
  }
  if (localPart.length > 64) {
    return { isValid: false, message: "La parte antes del @ no puede tener más de 64 caracteres" };
  }

  // 8. Validar caracteres permitidos en parte local: a-z A-Z 0-9 . _ % + -
  if (!/^[a-zA-Z0-9._%+-]+$/.test(localPart)) {
    return { isValid: false, message: "El correo contiene caracteres no permitidos antes del @" };
  }

  // 9. Validar que la parte local no empieza ni termina con punto
  // Regla técnica: la parte local (antes del @) no puede comenzar ni terminar con "."
  // Mensaje simplificado para usuarios no técnicos en flujo de compra
  if (localPart.startsWith(".") || localPart.endsWith(".")) {
    return { isValid: false, message: "Tu correo no puede empezar ni terminar con punto" };
  }

  // 10. Validar que no hay puntos consecutivos en parte local
  if (localPart.includes("..")) {
    return { isValid: false, message: "El correo no puede tener puntos consecutivos" };
  }

  // 11. Validar dominio (después del @)
  if (domain.length === 0) {
    return { isValid: false, message: "Debe haber texto después del @" };
  }

  // Regla técnica: el dominio debe contener al menos un "." (ej: gmail.com)
  // Mensaje simplificado para usuarios no técnicos en flujo de compra
  if (!domain.includes(".")) {
    return { isValid: false, message: "Después del @ debe ir algo como gmail.com" };
  }
  // Regla técnica: el dominio solo acepta caracteres alfanuméricos, puntos y guiones
  // Mensaje simplificado para usuarios no técnicos en flujo de compra
  if (!/^[a-zA-Z0-9.-]+$/.test(domain)) {
    return { isValid: false, message: "Después del @ solo puede haber letras, números y puntos" };
  }

  // 14. Validar que el dominio no empieza ni termina con punto
  if (domain.startsWith(".") || domain.endsWith(".")) {
    return { isValid: false, message: "El dominio no puede empezar ni terminar con punto" };
  }

  // 15. Validar que no hay puntos consecutivos en dominio
  if (domain.includes("..")) {
    return { isValid: false, message: "El dominio no puede tener puntos consecutivos" };
  }

  // 16. Validar cada segmento del dominio (no puede empezar/terminar con guion)
  const domainParts = domain.split(".");
  for (let part of domainParts) {
    // Regla técnica: no puede haber segmentos vacíos entre puntos (ej: gmail..com)
    // Mensaje simplificado para usuarios no técnicos en flujo de compra
    if (part.length === 0) {
      return { isValid: false, message: "Revisa los puntos después del @" };
    }
    // Regla técnica: cada segmento del dominio no puede comenzar ni terminar con guion
    // Mensaje simplificado para usuarios no técnicos en flujo de compra
    if (part.startsWith("-") || part.endsWith("-")) {
      return { isValid: false, message: "Revisa los guiones después del @" };
    }
  }
  const tld = domainParts[domainParts.length - 1];
  // Regla técnica: el TLD (ej: .com, .cl) debe tener entre 2 y 63 caracteres y solo letras
  // Mensajes simplificados para usuarios no técnicos en flujo de compra
  if (tld.length < 2) {
    return { isValid: false, message: "Falta la terminación del correo, ej: .com" };
  }
  if (tld.length > 63) {
    return { isValid: false, message: "El final del correo es demasiado largo" };
  }
  if (!/^[a-zA-Z]+$/.test(tld)) {
    return { isValid: false, message: "El final del correo solo puede tener letras, ej: .com, .cl" };
  }

  // ✅ Todo correcto
  return { isValid: true, message: "" };
};

const isValidEmail = (email, validationResult = null) => {
  // Solo permitir asteriscos si es un paciente registrado con datos anonimizados
  if (isAnonymizedValue(email) && email.includes('@') && email.includes('.')) {
    const isPacienteRegistrado = validationResult?.paciente === "paciente_registrado";
    if (isPacienteRegistrado) {
      return true; // Email anonimizado del backend
    }
    // Si tiene asteriscos pero NO es paciente registrado, rechazar
    return false;
  }
  return validateEmail(email).isValid;
};

const getEmailValidationMessage = (email, validationResult = null) => {
  // Solo permitir asteriscos si es un paciente registrado con datos anonimizados
  if (isAnonymizedValue(email) && email.includes('@') && email.includes('.')) {
    const isPacienteRegistrado = validationResult?.paciente === "paciente_registrado";
    if (isPacienteRegistrado) {
      return ""; // Email anonimizado del backend, sin error
    }
    // Si tiene asteriscos pero NO es paciente registrado, mostrar error
    return "El correo no puede contener asteriscos";
  }
  return validateEmail(email).message;
};

const areEmailsEqual = (email1, email2) => {
  return email1.toLowerCase() === (email2 || "").toLowerCase();
};

const isValidPhone = (phoneData) => {
  if (!phoneData || !phoneData.countryCode || !phoneData.number) return false;
  
  // Si el número está anonimizado, considerarlo válido
  if (isAnonymizedValue(phoneData.number)) return true;
  
  const country = COUNTRIES.find(c => c.code === phoneData.countryCode);
  if (!country) return false;
  return phoneData.number.length === country.digits && /^\d+$/.test(phoneData.number);
};

const isValidRut = (rut, formatoRut) => {
  if (formatoRut) {
    return rut.length >= 9 && rut.length <= 12;
  } else {
    return rut.length >= 1;
  }
};

// Función para obtener el primer mensaje de alerta en orden
const getValidationAlert = (rut, formatoRut, isRutValidated, nombre, apellido, email, confirmEmail, telefono, direccion, comunaCobertura, validationResult, isDomicilio = false) => {
  // 1. Validar RUT
  if (!isValidRut(rut, formatoRut)) {
    return formatoRut ? "Ingrese un RUT válido" : "Ingrese un pasaporte válido";
  }
  
  if (!isRutValidated) {
    return "Debe validar su RUT o pasaporte";
  }
  
  const isPacienteRegistrado = validationResult?.paciente === "paciente_registrado";
  
  // 2. Validar nombre (saltar si es paciente registrado y anonimizado)
  if (!isValidText(nombre) && !(isPacienteRegistrado && isAnonymizedValue(nombre))) {
    return "Ingrese su nombre";
  }
  
  // 3. Validar apellido (saltar si es paciente registrado y anonimizado)
  if (!isValidText(apellido) && !(isPacienteRegistrado && isAnonymizedValue(apellido))) {
    return "Ingrese su apellido";
  }
  
  // 4. Validar email (saltar si es paciente registrado y anonimizado)
  if (!(isPacienteRegistrado && isAnonymizedValue(email))) {
    const emailMessage = getEmailValidationMessage(email, validationResult);
    if (emailMessage) {
      return emailMessage;
    }
  }
  
  // 5. Validar confirmación de email (saltar si es paciente registrado y anonimizado)
  if (!(isPacienteRegistrado && isAnonymizedValue(email))) {
    if (!confirmEmail) {
      return "Confirme su correo electrónico";
    }
    
    if (email && !areEmailsEqual(email, confirmEmail)) {
      return "Los correos electrónicos no son iguales";
    }
  }
  
  // 6. Validar teléfono (saltar si es paciente registrado y anonimizado)
  if (!isValidPhone(telefono) && !(isPacienteRegistrado && isAnonymizedValue(telefono?.number))) {
    const country = COUNTRIES.find(c => c.code === telefono?.countryCode);
    const digits = country ? country.digits : 9;
    return `El teléfono debe tener ${digits} dígitos después de ${telefono?.countryCode || "+56"}`;
  }
  
  // 7. Validar dirección y cobertura SOLO si el servicio es a domicilio (también para pacientes registrados)
  if (isDomicilio) {
    if (!direccion.trim()) {
      return "Ingrese su dirección";
    }
    
    if (!comunaCobertura) {
      return "La comuna seleccionada no tiene cobertura para este servicio";
    }
  }
  
  return "";
};

// Loader inline component
const LoaderOverlay = ({ isLoading }) => {
  if (!isLoading) return null;
  
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <CircularProgress sx={{ color: "#3B82F6" }} />
    </Box>
  );
};

// CustomAlert inline component para validación RUT
const CustomAlert = ({ type, nombre }) => {
  if (!type) return null;

  const alertStyles = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px",
    borderRadius: "4px",
    fontSize: { md: "14px", xs: "12px" },
    fontFamily: "Quicksand-Regular",
    marginTop: "8px",
  };

  const alertConfigs = {
    datos_existentes: {
      backgroundColor: "#f0f4ff",
      color: "#3B82F6",
      getMessage: (nombre) => ({
        title: `Bienvenido de nuevo ${nombre || ""}`,
        subtitle: "tus datos están listos para agendar",
      }),
    },
    paciente_nuevo: {
      backgroundColor: "#f0f4ff",
      color: "#3B82F6",
      icon: Done,  // ← Icono MUI Done
      getMessage: () => ({
        title: "Identificación válida",
        subtitle: "ingrese sus datos para agendar",
      }),
    },
    rut_invalido: {
      backgroundColor: "#FFEBEE",
      color: "#C62828",
      icon: PriorityHigh,  // ← Icono MUI PriorityHigh
      getMessage: () => ({
        title: "Identificación inválida",
        subtitle: "Por favor intenta nuevamente",
      }),
    },
  };

  const config = alertConfigs[type];
  if (!config) return null;

  const message = config.getMessage(nombre);

  return (
    <Box
      sx={{
        ...alertStyles,
        backgroundColor: config.backgroundColor,
        color: config.color,
      }}
    >
      {type === "datos_existentes" && (
        <Box sx={{ width: { md: "28px", xs: "26px" }, height: { md: "28px", xs: "26px" } }}>
          <img
            src="/sparkles.svg"
            alt="Estrellas"
            style={{
              width: "100%",
              height: "100%",
              filter: "invert(35%) sepia(97%) saturate(3730%) hue-rotate(214deg) brightness(96%) contrast(93%)",
            }}
          />
        </Box>
      )}
      {config.icon && (
        <config.icon 
          sx={{ 
            fontSize: { md: "28px", xs: "26px" },  // ← Más grande que el 22px anterior
            color: config.color,
            fontWeight: 'bold'
          }} 
        />
      )}
      <Box>
        <Typography
          sx={{
            fontFamily: "Quicksand-Bold",
            fontSize: { md: "15px", xs: "13px" },
            lineHeight: 1.2,
          }}
        >
          {message.title}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { md: "12px", xs: "11px" },
            lineHeight: 1.2,
          }}
        >
          {message.subtitle}
        </Typography>
      </Box>
    </Box>
  );
};

// MobileFriendlySelect component
const MobileFriendlySelect = ({ value, onChange, options, placeholder, disabled, sx = {}, IconComponent, isMobile }) => {
  const baseSelectStyles = {
    height: "22px !important",
    maxHeight: "22px !important",
    minHeight: "22px !important",
    width: "100%",
    fontFamily: "Quicksand-Regular",
    fontSize: { md: "16px", xs: "14px" },
    backgroundColor: "transparent",
    border: "none",
    margin: "0 !important",
    WebkitAppearance: isMobile ? "menulist" : "none",
    MozAppearance: isMobile ? "menulist" : "none",
    appearance: isMobile ? "menulist" : "none",
    "& select": {
      width: "100%",
      backgroundColor: "transparent",
      border: "none",
      outline: "none",
      fontSize: "inherit",
      fontFamily: "inherit",
      color: disabled ? "#9e9e9e" : "#4F4F4F",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
    }
  };

  if (isMobile) {
    return (
      <Box sx={{ ...baseSelectStyles, ...sx }}>
        <select 
          value={value || ""} 
          onChange={(e) => onChange({ target: { value: e.target.value } })}
          disabled={disabled}
          style={{
            width: "100%",
            backgroundColor: "transparent",
            border: "none",
            outline: "none",
            fontSize: "inherit",
            fontFamily: "inherit",
            color: disabled ? "#9e9e9e" : "#4F4F4F",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <option value="" disabled>{placeholder}</option>
          {options.map((option) => (
            <option 
              key={option.value} 
              value={option.value}
              style={{
                fontFamily: "Quicksand-Regular",
                fontSize: "14px",
              }}
            >
              {option.label}
            </option>
          ))}
        </select>
      </Box>
    );
  }

  // Desktop version using MUI Select
  const muiBaseSelectStyles = {
    height: "22px !important",
    maxHeight: "22px !important",
    minHeight: "22px !important",
    width: "100%",
    fontFamily: "Quicksand-Regular",
    fontSize: { md: "16px", xs: "14px" },
    backgroundColor: "transparent",
    border: "none",
    margin: "0 !important",
    "& .MuiOutlinedInput-root": {
      height: "22px !important",
      maxHeight: "22px !important",
      minHeight: "22px !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiSelect-select": {
      padding: "0 !important",
      paddingLeft: "4px !important",
      paddingRight: "24px !important",
      lineHeight: "22px !important",
      minHeight: "22px !important",
      maxHeight: "22px !important",
      height: "22px !important",
      display: "flex !important",
      alignItems: "center !important",
      margin: "0 !important",
    },
    "& .MuiSelect-icon": {
      top: "calc(50% - 12px) !important",
    }
  };

  const disabledStyles = {
    pointerEvents: "none",
    color: "#9e9e9e",
    "&.Mui-disabled": {
      backgroundColor: "#f5f5f5",
      opacity: 0.6,
      "& .MuiSelect-select": {
        WebkitTextFillColor: "#9e9e9e",
        cursor: "not-allowed",
      },
    },
    "& .MuiSelect-icon": {
      opacity: 0.6,
      color: "#9e9e9e",
    },
  };

  const enabledStyles = {
    color: "#4F4F4F",
    "& .MuiSelect-select": {
      cursor: "pointer",
    },
    "& .MuiSelect-icon": {
      opacity: 1,
      color: "#4F4F4F",
    },
  };

  return (
    <Select
      value={value}
      onChange={onChange}
      displayEmpty
      disabled={disabled}
      IconComponent={IconComponent}
      sx={{
        ...muiBaseSelectStyles,
        ...(disabled ? disabledStyles : enabledStyles),
        ...sx,
      }}
    >
      <MenuItem value="" disabled>
        {placeholder}
      </MenuItem>
      {options.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { md: "14px", xs: "12px" },
            "&.Mui-disabled": {
              opacity: 0.6,
            },
          }}
        >
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};

// StyledSelect component (now a wrapper for MobileFriendlySelect)
const StyledSelect = (props) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  return <MobileFriendlySelect {...props} isMobile={isMobile} />;
};

// AddressInput inline component
const AddressInput = ({
  onAddressChange,
  onComunaChange,
  disabled,
  direccion: direccionProp,
}) => {
  const [direccionLocal, setDireccionLocal] = useState(direccionProp || "");
  const [sugerencias, setSugerencias] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (direccionProp !== undefined) {
      setDireccionLocal(direccionProp);
    }
  }, [direccionProp]);

  const obtenerSugerencias = useCallback(
    debounce((value) => {
      if (disabled || !value) {
        setSugerencias([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: "CL" },
        },
        (predictions, status) => {
          setLoading(false);
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setSugerencias(
              predictions.map((prediction) => ({
                description: prediction.description,
                place_id: prediction.place_id,
              }))
            );
          } else {
            setSugerencias([]);
          }
        }
      );
    }, 10),
    [disabled]
  );

  const handleInputChange = (event, newValue) => {
    if (disabled) return;
    if (typeof newValue === "string") {
      const limitedValue = newValue.slice(0, 100);
      setDireccionLocal(limitedValue);
      onAddressChange(limitedValue);
      
      // Establecer comuna por defecto para texto libre
      if (limitedValue.trim()) {
        onComunaChange("comuna no detectada");
      }
      
      obtenerSugerencias(limitedValue);
    }
  };

  const handleSelectionChange = (event, newValue) => {
    if (disabled) return;
    if (newValue && newValue.description) {
      const limitedValue = newValue.description.slice(0, 100);
      setDireccionLocal(limitedValue);
      onAddressChange(limitedValue);
  
      const placesService = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
  
      placesService.getDetails(
        { placeId: newValue.place_id },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            const comunaComponent = place.address_components.find(
              (component) =>
                component.types.includes("administrative_area_level_3") ||
                component.types.includes("locality")
            );
            if (comunaComponent) {
              onComunaChange(comunaComponent.long_name);
            }
          }
        }
      );
    }
  };

  const handleBlur = () => {
    if (disabled || !direccionLocal) return;
  
    const autocompleteService =
      new window.google.maps.places.AutocompleteService();
    autocompleteService.getPlacePredictions(
      { input: direccionLocal },
      (predictions, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions.length > 0
        ) {
          const placesService = new window.google.maps.places.PlacesService(
            document.createElement("div")
          );
          placesService.getDetails(
            { placeId: predictions[0].place_id },
            (place, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                const comunaComponent = place.address_components.find(
                  (component) =>
                    component.types.includes("administrative_area_level_3") ||
                    component.types.includes("locality")
                );
                if (comunaComponent) {
                  onComunaChange(comunaComponent.long_name);
                }
              }
            }
          );
        } else {
          // Fallback: mantener "Sin comuna detectada" si no se encuentra nada
          onComunaChange("Sin comuna detectada");
        }
      }
    );
  };

  return (
    <>
      <Typography
        sx={{
          pt: 1,
          fontFamily:
            direccionLocal.trim() !== ""
              ? "Quicksand-Bold"
              : "Quicksand-Regular",
          fontSize: { md: "16px", xs: "14px" },
          color: disabled
            ? "#9e9e9e"
            : direccionLocal.trim() !== ""
            ? "#2669A8"
            : "#191C1F",
        }}
      >
        {direccionLocal.trim() !== "" ? "Dirección" : "Dirección*"}
      </Typography>
      <Box
        sx={{
          borderRadius: 2,
          border: "1px solid #909090",
          backgroundColor: disabled ? "#f5f5f5" : "white",
          opacity: disabled ? 0.6 : 1,
          paddingTop: 0.5,
          paddingBottom: 0.5,
          paddingLeft: 0.5,
          paddingRight: 0.5,
          "&:focus-within": {
            borderColor: "#3B82F6",
            boxShadow: "0 0 0 1px #3B82F6",
          },
        }}
      >
        <Autocomplete
          freeSolo
          loading={loading}
          disabled={disabled}
          options={sugerencias}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.description
          }
          inputValue={direccionLocal}
          onInputChange={handleInputChange}
          onChange={handleSelectionChange}
          onBlur={handleBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              inputProps={{
                ...params.inputProps,
                maxLength: 100,
                style: {
                  fontFamily: "Quicksand-Regular",
                  fontSize: "16px",
                  color: "#4F4F4F",
                  cursor: disabled ? "not-allowed" : "text",
                  padding: "0",
                  paddingLeft: "0px",
                },
              }}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                sx: {
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontFamily: "Quicksand-Regular",
                  color: "#4F4F4F",
                  fontSize: { md: "16px", xs: "14px" },
                  "& fieldset": {
                    borderColor: "transparent",
                    borderWidth: 0,
                  },
                  "&:hover fieldset": {
                    borderColor: "transparent",
                    borderWidth: 0,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "transparent",
                    borderWidth: 0,
                  },
                  "&.Mui-disabled fieldset": {
                    borderColor: "transparent",
                    borderWidth: 0,
                  },
                  "& input": {
                    padding: 0,
                  },
                },
              }}
            />
          )}
          noOptionsText="No hay sugerencias"
          loadingText="Buscando..."
          filterOptions={(x) => x}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                borderBottom: "1px solid #e0e0e0",
                py: 1,
                fontSize: { md: "16px", xs: "14px" },
                fontFamily: "Quicksand-Regular",
              }}
            >
              {option.description}
            </Box>
          )}
          sx={{
            minHeight: "22px",
            "& .MuiInputBase-root": {
              minHeight: "22px",
            },
            "&.Mui-disabled": {
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            },
          }}
        />
      </Box>
    </>
  );
};

// PaymentMethodBox inline component
const BlueCheckbox = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.grey[400],
  "&.Mui-checked": {
    color: "#1976d2",
  },
  "& .MuiSvgIcon-root": {
    fontSize: 28,
  },
}));

const PaymentMethodBox = ({
  onPaymentMethodChange,
  tarjeta,
  transferencia,
  selectedMethod,
}) => {
  const handleMethodChange = (method) => {
    if (onPaymentMethodChange) {
      onPaymentMethodChange(method);
    }
  };

  const handleCheckboxClick = (event, method) => {
    event.stopPropagation();
    handleMethodChange(method);
  };

  return (
    <>
      {tarjeta === true && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            paddingTop: 1,
            paddingBottom: 1,
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            width: "100%",
            cursor: "pointer",
            backgroundColor:
              selectedMethod === "webpay" ? "#f5f5f5" : "transparent",
          }}
          onClick={() => handleMethodChange("webpay")}
        >
          <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
            <img
              src={logoWebpay}
              alt="Webpay Logo"
              style={{ height: "24px", marginRight: "12px" }}
            />
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { md: "16px", xs: "14px" },
                color: "#191C1F",
              }}
            >
              Tarjeta de crédito o débito
            </Typography>
          </Box>
          <BlueCheckbox
            checked={selectedMethod === "webpay"}
            onClick={(e) => handleCheckboxClick(e, "webpay")}
            sx={{ mr: 1 }}
          />
        </Box>
      )}

      {tarjeta === true && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            paddingTop: 1,
            paddingBottom: 1,
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            width: "100%",
            cursor: "pointer",
            backgroundColor:
              selectedMethod === "mercadopago" ? "#f5f5f5" : "transparent",
          }}
          onClick={() => handleMethodChange("mercadopago")}
        >
          <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
            <img
              src={logoMercadoPago}
              alt="MercadoPago Logo"
              style={{ height: "24px", marginRight: "12px" }}
            />
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { md: "16px", xs: "14px" },
                color: "#191C1F",
              }}
            >
              Tarjeta de crédito o débito
            </Typography>
          </Box>
          <BlueCheckbox
            checked={selectedMethod === "mercadopago"}
            onClick={(e) => handleCheckboxClick(e, "mercadopago")}
            sx={{ mr: 1 }}
          />
        </Box>
      )}

      {transferencia === true && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            paddingTop: 1,
            paddingBottom: 1,
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            width: "100%",
            cursor: "pointer",
            backgroundColor:
              selectedMethod === "transfer" ? "#f5f5f5" : "transparent",
          }}
          onClick={() => handleMethodChange("transfer")}
        >
          <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
            <img
              src={logoImage}
              alt="Bank Transfer Logo"
              style={{ height: "24px", marginRight: "12px" }}
            />
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { md: "16px", xs: "14px" },
                display: "inline-block",
                px: 0.5,
                color: "#191C1F",
              }}
            >
              Transferencia todos los bancos
            </Typography>
          </Box>
          <BlueCheckbox
            checked={selectedMethod === "transfer"}
            onClick={(e) => handleCheckboxClick(e, "transfer")}
            sx={{ mr: 1 }}
          />
        </Box>
      )}
    </>
  );
};

// RutValidationGrid inline component
const RutValidationGrid = ({
  rut,
  handleChangeRut,
  isRutValidated,
  formatoRut,
  handleCheckboxChange,
  validationResult,
}) => {
  const [showPassportCheck, setShowPassportCheck] = useState(false);

  const handlePassportClick = () => {
    if (!showPassportCheck && !isRutValidated) {
      setShowPassportCheck(true);
    }
  };

  return (
    <Grid item xs={12} lg={12}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            pt: 1,
            fontFamily: isValidRut(rut, formatoRut)
              ? "Quicksand-Bold"
              : "Quicksand-Regular",
            fontSize: { md: "16px", xs: "14px" },
            color: isValidRut(rut, formatoRut) ? "#2669A8" : "#191C1F",
          }}
        >
          {isValidRut(rut, formatoRut)
            ? formatoRut
              ? "RUT"
              : "Pasaporte"
            : formatoRut
            ? "RUT*"
            : "Pasaporte*"}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: isRutValidated ? "not-allowed" : "pointer",
            opacity: isRutValidated ? 0.6 : 1,
            height: "24px",
            minHeight: "24px",
          }}
          onClick={handlePassportClick}
        >
          <Typography
            sx={{
              fontFamily: !formatoRut ? "Quicksand-Bold" : "Quicksand-Regular",
              color: isRutValidated
                ? "#9e9e9e"
                : !formatoRut
                ? "#3B82F6"
                : "#9e9e9e",
              fontSize: { md: "14px", xs: "12px" },
              pointerEvents: isRutValidated ? "none" : "auto",
            }}
          >
            Pasaporte
          </Typography>
          {showPassportCheck && (
            <Checkbox
              checked={!formatoRut}
              onChange={(e) => !isRutValidated && handleCheckboxChange(e)}
              name="esPasaporte"
              disabled={isRutValidated}
              sx={{
                color: isRutValidated ? "#9e9e9e" : "#3B82F6",
                "&.Mui-checked": {
                  color: isRutValidated ? "#9e9e9e" : "#3B82F6",
                },
                cursor: isRutValidated ? "not-allowed" : "pointer",
                padding: 0,
                paddingLeft: 1,
                margin: 0,
                "& .MuiSvgIcon-root": {
                  fontSize: "16px",
                },
              }}
            />
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          borderRadius: 2,
          border: "1px solid #909090",
          backgroundColor: isRutValidated ? "#f5f5f5" : "white",
          opacity: isRutValidated ? 0.6 : 1,
          p: 0.5,
          "&:focus-within": {
            borderColor: "#3B82F6",
            boxShadow: "0 0 0 1px #3B82F6",
          },
        }}
      >
        <TextField
          id="rut"
          name="rut"
          value={rut}
          onChange={handleChangeRut}
          disabled={isRutValidated}
          inputProps={{ maxLength: formatoRut ? 12 : 25 }}
          variant="standard"
          fullWidth
          InputProps={{
            disableUnderline: true,
            sx: {
              border: "none",
              outline: "none",
              background: "transparent",
              fontFamily: "Quicksand-Regular",
              color: "#4F4F4F",
              fontSize: { md: "16px", xs: "14px" },
              "& input": {
                padding: 0,
              },
            },
          }}
        />
      </Box>

      <CustomAlert
        type={
          validationResult?.validacion === "rut_valido"
            ? validationResult.paciente === "paciente_registrado"
              ? "datos_existentes"
              : "paciente_nuevo"
            : validationResult?.validacion === "rut_invalido"
            ? "rut_invalido"
            : null
        }
        nombre={validationResult?.datos_paciente?.nombres}
      />
    </Grid>
  );
};

export default function DatosPaciente() {
  const navigate = useNavigate();
  const location = useLocation();
  const MySwal = withReactContent(Swal);

  // Estados principales
  const [nombre, setNombre] = useState("");
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [apellido, setApellido] = useState("");
  const [preferenceId, setPreferenceId] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [text, setText] = useState("");
  const [mail, setMail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [telefono, setTelefono] = useState({ countryCode: "+56", number: "" });
  const [rut, setRut] = React.useState("");
  const [formatoRut, setFormatoRut] = useState(true);
  const [ApiCrear, setApiCrear] = useState(null);
  const [depto, setDepto] = React.useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [isRutValidated, setIsRutValidated] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [savedData, setSavedData] = useState(null);
  const [id_paciente, setId_paciente] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(
    location.state.perfil_pago_tarjeta ? "webpay" : "transfer"
  );
  const [confirmMail, setConfirmMail] = useState("");
  const [transbankData, setTransbankData] = useState({
    session_id: null,
    token: null,
    url: null,
    buy_order: null
  });
  const paymentFormRef = useRef(null);
  const esServicioGratuito = location.state.servicio_precio_gratuito === 1;

  // Estados para dirección
  const [direccion, setDireccion] = useState("");
  const [comuna, setComuna] = useState("");
  const [comunaCobertura, setComunaCobertura] = useState(true);
  const [comunasAtendidas, setComunasAtendidas] = useState([]);

  // Estados para MercadoPago
  const [shouldShowWallet, setShouldShowWallet] = useState(false);
  const [isWalletReady, setIsWalletReady] = useState(false);
  const walletRef = useRef(null);

  // Estados para cupones de descuento
  const [cuponInput, setCuponInput] = useState("");
  const [cuponAplicado, setCuponAplicado] = useState(false);
  const [tipoMensajeCupon, setTipoMensajeCupon] = useState(null);
  const [descuentoMonto, setDescuentoMonto] = useState(0);
  // Nuevos estados para cupones automáticos
  const [cuponAutomatico, setCuponAutomatico] = useState(false);
  const [cuponValidandose, setCuponValidandose] = useState(false);
  const [precioOriginalCupon, setPrecioOriginalCupon] = useState(0);
  const [descuentoAplicadoCupon, setDescuentoAplicadoCupon] = useState(0);
  const [precioFinalCupon, setPrecioFinalCupon] = useState(0);

  
  // Texto animado
  const textseciones = "✨ Ingrese los siguientes datos para agendar";


  // Efecto para comunas atendidas
  useEffect(() => {
    if (location.state.cobertura_domicilio) {
      setComunasAtendidas(location.state.cobertura_domicilio);
    }
  }, [location.state.cobertura_domicilio]);

  // Scroll to top al cargar la página
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ← AGREGAR TODO ESTO
  useEffect(() => {
    if (window.google && window.google.maps) {
      setIsGoogleMapsLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyCVx9QehzlqYZV276ju0jiN0zxOQmLNOAY&libraries=places';
    script.async = true;
    script.onload = () => setIsGoogleMapsLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Validación de cobertura
  const validarCobertura = (comunaSeleccionada) => {
    if (location.state.servicio_modalidad !== "domicilio") {
      setComunaCobertura(true);
      return;
    }
  
    // Manejar comuna sin detectar
    if (comunaSeleccionada === "Sin comuna detectada") {
      setComunaCobertura(false);
      return;
    }
  
    if (!comunaSeleccionada || !comunasAtendidas.length) {
      setComunaCobertura(true);
      return;
    }
  
    const normalizarString = (str) => {
      return str
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
    };
  
    const comunaNormalizada = normalizarString(comunaSeleccionada);
    const comunasNormalizadas = comunasAtendidas.map((comuna) =>
      normalizarString(comuna)
    );
  
    const tieneCobertura = comunasNormalizadas.includes(comunaNormalizada);
    setComunaCobertura(tieneCobertura);
  };

  const handleComunaChange = (nuevaComuna) => {
    setComuna(nuevaComuna);
    validarCobertura(nuevaComuna);
  };

  const isDomicilio = location.state.servicio_modalidad === "domicilio";

  // Validación del formulario
  const isFormValid = useMemo(() => {
    const baseValid =
      isValidText(nombre) &&
      isValidText(apellido) &&
      isValidRut(rut, formatoRut) &&
      isRutValidated &&
      isValidEmail(mail, validationResult) &&
      mail === confirmMail &&
      isValidPhone(telefono);

    if (isDomicilio) {
      return (
        baseValid &&
        direccion.trim() !== "" &&
        comunaCobertura
      );
    }

    return baseValid;
  }, [
    nombre,
    apellido,
    rut,
    formatoRut,
    isRutValidated,
    mail,
    confirmMail,
    telefono,
    direccion,
    comunaCobertura,
    isDomicilio,
    validationResult,
  ]);

  // Cálculo del precio final con descuento
  // Si hay cupón aplicado, usar precios del backend; si no, calcular localmente
  const precioOriginal = cuponAplicado 
    ? precioOriginalCupon 
    : parseInt(location.state.servicio_precios.replace(/[^\d]/g, '')) || 0;

  const descuentoActual = cuponAplicado 
    ? descuentoAplicadoCupon 
    : 0;  // ← CAMBIO: Cuando no hay cupón, descuento es 0

  const precioFinal = cuponAplicado 
    ? precioFinalCupon 
    : precioOriginal;

  const esReservaGratuitaPorCupon = !esServicioGratuito && cuponAplicado && precioFinal === 0;

  // Funciones de formato
  const formatearFechaSinAnio = (fecha) => {
    return fecha.slice(0, -5);
  };

  const transformModalidad = (modalidad) => {
    switch (modalidad) {
      case "consultorio":
        return "Consulta presencial";
      case "domicilio":
        return "Consulta a domicilio";
      case "online":
        return "Consulta online";
      default:
        return modalidad;
    }
  };

  const formatRut = (rut) => {
    let cleanRut = rut.replace(/[^0-9kK]+/g, "").toUpperCase();

    if (cleanRut.length > 1) {
      const body = cleanRut.slice(0, -1).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      const dv = cleanRut.slice(-1);
      cleanRut = body + "-" + dv;
    }

    return cleanRut;
  };

  // Handlers
  const handleChangeNombre = (event) => {
    const limitedValue = event.target.value.slice(0, 50);
    setNombre(limitedValue);
  };

  const handleChangeApellido = (event) => {
    const limitedValue = event.target.value.slice(0, 50);
    setApellido(limitedValue);
  };

  const handleChangeDiagnostico = (event) => {
    const limitedValue = event.target.value.slice(0, 250);
    setText(limitedValue);
  };

  const handleCheckboxChange = (event) => {
    setFormatoRut(!event.target.checked);
  };

  const handleChangeMail = (event) => {
    const limitedValue = event.target.value.slice(0, 50);
    setMail(limitedValue);
  };

  const handleChangeConfirmMail = (event) => {
    const limitedValue = event.target.value.slice(0, 50);
    setConfirmMail(limitedValue);
  };

  const handleChangeRut = (event) => {
    const value = event.target.value;
    if (formatoRut) {
      const formattedRut = formatRut(value);
      setRut(formattedRut);
    } else {
      setRut(value);
    }
  };

  const handleChangeCountry = (event) => {
    setTelefono({ countryCode: event.target.value, number: "" });
  };

  const handleChangeTelefono = (event) => {
    let inputValue = event.target.value.replace(/\D/g, "");
    const country = COUNTRIES.find(c => c.code === telefono.countryCode);
    const maxLength = country ? country.digits : 9;
    inputValue = inputValue.slice(0, maxLength);
    setTelefono({ ...telefono, number: inputValue });
  };

  const handleChangeDepto = (event) => {
    const limitedValue = event.target.value.slice(0, 50);
    setDepto(limitedValue);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
  };

  // Handlers para cupones de descuento
  const handleCuponInputChange = (event) => {
    const value = event.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 20);
    setCuponInput(value);
    // Resetear tipo de mensaje cuando el usuario modifica el texto
    if (tipoMensajeCupon !== null) {
      setTipoMensajeCupon(null);
    }
  };

  const handleAplicarCupon = () => {
    if (!cuponInput.trim()) {
      return; // No hacer nada si el input está vacío
    }

    // Llamar a la función de validación que consulta al backend
    validarCupon(cuponInput);
  };

  // Funciones de estilos
  const isFieldDisabled = () => !isRutValidated || (savedData && !isEditing);

  // Dirección y Depto (solo domicilio) quedan editables aunque el paciente esté registrado, ya que la dirección guardada llega anonimizada
  const isAddressDisabled = () => !isRutValidated;

  const getFieldContainerStyle = (disabled = isFieldDisabled()) => ({
    borderRadius: 2,
    border: "1px solid #909090",
    backgroundColor: disabled ? "#f5f5f5" : "white",
    opacity: disabled ? 0.6 : 1,
    p: 0.5,
    "&:focus-within": {
      borderColor: "#3B82F6",
      boxShadow: "0 0 0 1px #3B82F6",
    },
  });

  const baseInputStyle = {
    border: "none",
    width: "100%",
    outline: "none",
    background: "transparent",
    fontFamily: "Quicksand-Regular",
    color: "#4F4F4F",
    fontSize: "16px",
    cursor: isFieldDisabled() ? "not-allowed" : "text",
  };



  // Funciones de API
  const identificarCuponAutomatico = async (idPaciente) => {
    try {
      // Usar el id_paciente recibido como parámetro
      if (!idPaciente) {
        return; // No ejecutar si no hay id_paciente
      }

      // Construir URL con parámetros requeridos
      const params = new URLSearchParams({
        id_profesional_fantasma: location.state.id_kinesiologo,
        id_servicio_fantasma: location.state.id_servicios,
        id_paciente: idPaciente,
      });

      const url = `/agenda/identificarCupon?${params.toString()}`;
      const result = await invokeApi(url);

      // Manejar respuesta de invokeApi que puede venir en result.error.serverData
      let actualResult = result;
      if (result.error && result.error.serverData) {
        actualResult = result.error.serverData;
      } else if (result.error) {
        // Error real de red, continuar sin cupón silenciosamente
        console.log("Error al identificar cupón, continuando sin cupón");
        return;
      }

      // Si tiene cupón disponible, auto-completar y validar
      if (actualResult?.tiene_cupon === 1 && actualResult?.codigo_mejor_cupon) {
        setCuponInput(actualResult.codigo_mejor_cupon);
        setCuponAutomatico(true);
        // Validar automáticamente el cupón identificado con el id_paciente
        validarCupon(actualResult.codigo_mejor_cupon, idPaciente);
      }
    } catch (error) {
      // Error silencioso, no afectar experiencia del usuario
      console.log("Error al identificar cupón automáticamente:", error);
    }
  };


  const validarCupon = async (codigo, idPacienteParam = null) => {
    setCuponValidandose(true);
    try {
      // Construir URL con parámetros requeridos
      const params = new URLSearchParams({
        id_profesional_fantasma: location.state.id_kinesiologo,
        id_servicio_fantasma: location.state.id_servicios,
        cupon_codigo: codigo,
      });

      // Agregar id_paciente si fue pasado como parámetro
      if (idPacienteParam) {
        params.append("id_paciente", idPacienteParam);
      }
      // O si está disponible en validationResult (flujo manual)
      else if (validationResult?.paciente === "paciente_registrado" && 
               validationResult?.datos_paciente?.id_paciente) {
        params.append("id_paciente", validationResult.datos_paciente.id_paciente);
      }

      const url = `/agenda/validarCupon?${params.toString()}`;
      const result = await invokeApi(url);

      // Manejar respuesta de invokeApi
      let actualResult = result;
      if (result.error && result.error.serverData) {
        actualResult = result.error.serverData;
      } else if (result.error) {
        // Error de validación
        console.error("Error al validar cupón:", result.error);
        setTipoMensajeCupon("invalido");
        setCuponAplicado(false);
        setPrecioOriginalCupon(0);
        setDescuentoAplicadoCupon(0);
        setPrecioFinalCupon(0);
        return;
      }

      // Si el cupón es válido, actualizar estados con precios del backend
      // Si el cupón es válido, actualizar estados con precios del backend
      if (actualResult?.valido === true) {
        setPrecioOriginalCupon(actualResult.precio_original);
        setDescuentoAplicadoCupon(actualResult.descuento_aplicado);
        setPrecioFinalCupon(actualResult.precio_pagar);
        setCuponAplicado(true);
        setTipoMensajeCupon("ok");
      } else {
          // Cupón inválido según backend - restaurar precios originales
          setPrecioOriginalCupon(0);
          setDescuentoAplicadoCupon(0);
          setPrecioFinalCupon(0);
          setCuponAplicado(false);
          
          // Distinguir tipo de error según mensaje del backend
          if (actualResult?.mensaje === "CUPON NO ENCONTRADO") {
            setTipoMensajeCupon("invalido");
          } else if (actualResult?.mensaje === "CUPON AGOTADO") {
            setTipoMensajeCupon("agotado");
          } else {
            setTipoMensajeCupon("invalido");
          }
        }
    } catch (error) {
        console.error("Error al validar cupón:", error);
        // Restaurar precios originales en caso de error
        setPrecioOriginalCupon(0);
        setDescuentoAplicadoCupon(0);
        setPrecioFinalCupon(0);
        setCuponAplicado(false);
        setTipoMensajeCupon("invalido");
    } finally {
      setCuponValidandose(false);
    }
  };

  const validateRut = async () => {
    setIsValidating(true);
    try {
      const url = `/agenda/validarIdentidad?id_profesional_fantasma=${
        location.state.id_kinesiologo
      }&paciente_rut=${rut}&paciente_es_pasaporte=${(!formatoRut).toString()}`;

      const result = await invokeApi(url);
      
      // Manejo especial para el endpoint validarIdentidad que no usa statusCode
      let actualResult = result;
      
      if (result.error && result.error.serverData && result.error.serverData.validacion) {
        // Si invokeApi interpretó como error pero contiene datos válidos de validación
        actualResult = result.error.serverData;
      } else if (result.error) {
        // Error real de red o servidor
        setValidationResult({ validacion: "rut_invalido" });
        setIsRutValidated(false);
        return;
      }
  
      setValidationResult(actualResult);
  
      if (actualResult?.validacion === "rut_valido") {
        if (
          actualResult.paciente === "paciente_registrado" &&
          Object.keys(actualResult.datos_paciente || {}).length > 0
        ) {
          const pacienteData = actualResult.datos_paciente;
          setSavedData(pacienteData);
          loadPatientData(pacienteData);
        }
        setIsRutValidated(true);
        
        // Identificar cupón automáticamente solo para pacientes registrados con id_paciente
        if (
          actualResult.paciente === "paciente_registrado" &&
          actualResult.datos_paciente?.id_paciente
        ) {
          // Ejecutar en background sin await (sin bloquear formulario)
          identificarCuponAutomatico(actualResult.datos_paciente.id_paciente);
        }
      } else {
        setIsRutValidated(false);
      }
    } catch (error) {
      console.error("Error validating RUT:", error);
      setValidationResult({ validacion: "rut_invalido" });
      setIsRutValidated(false);
    } finally {
      setIsValidating(false);
    }
  };

  const loadPatientData = (data) => {
    setId_paciente(data.id_paciente || null);
    setNombre(data.nombres || "");
    setApellido(data.apellidos || "");
    setMail(data.mail || "");
    setConfirmMail(data.mail || "");
    
    // Convertir teléfono de formato string a objeto
    if (data.celular) {
      const country = COUNTRIES.find(c => data.celular.startsWith(c.code));
      if (country) {
        setTelefono({
          countryCode: country.code,
          number: data.celular.replace(country.code, "")
        });
      } else {
        setTelefono({ countryCode: "+56", number: data.celular.replace("+56", "") });
      }
    }
    
    // La dirección y el depto guardados llegan anonimizados: no se precargan, se escriben siempre en servicios a domicilio
    setDireccion("");
    setDepto("");
  };

  const handleEditData = () => {
    if (isEditing) {
      if (savedData) {
        loadPatientData(savedData);
      }
    } else {
      setNombre("");
      setApellido("");
      setMail("");
      setConfirmMail("");
      setTelefono({ countryCode: "+56", number: "" });
      setDireccion("");
      setDepto("");
    }
    setIsEditing(!isEditing);
  };

  const createWebpayPayment = async () => {
  setIsLoading(true);

  try {
    const apiData = {
      id_profesional_fantasma: location.state.id_kinesiologo,
      id_servicio_fantasma: location.state.id_servicios,
    };
    
    if (validationResult?.paciente === "paciente_registrado" && id_paciente) {
      apiData.id_paciente = id_paciente;
    }
    
    if (cuponAplicado === true) {
      apiData.cupon_codigo = cuponInput;
    }
    
    const result = await invokeApi("/agenda/pagoCrearWebpay", "post", apiData);
    
    if (result.error) {
      MySwal.fire(result.error.message || "Error del servidor", "", "error");
      setIsLoading(false);
      return;
    }
    
    if (result && result.statusCode === 200 && result.transbank) {
      setTransbankData({
        session_id: result.transbank.session_id,
        token: result.transbank.token,
        url: result.transbank.url,
        buy_order: result.transbank.buy_order
      });
      handlePagarClick(null, null, result.transbank);
    } else {
      console.error("Respuesta inesperada:", result);
      MySwal.fire("Respuesta inesperada del servidor", "", "error");
      setIsLoading(false);
    }
  } catch (error) {
    setIsLoading(false);
    console.error("Error al crear pago Webpay:", error);
    MySwal.fire("Error al procesar el pago", "", "error");
  }
};

const createPayment = async () => {
  setIsLoading(true);

  try {
    const apiData = {
      id_profesional_fantasma: location.state.id_kinesiologo,
      id_servicio_fantasma: location.state.id_servicios,
      fecha_agenda: location.state.dates,
      hora_agenda: location.state.hourss,
      paciente_email: mail,
    };
    
    if (validationResult?.paciente === "paciente_registrado" && id_paciente) {
      apiData.id_paciente = id_paciente;
    }
    
    if (cuponAplicado === true) {
      apiData.cupon_codigo = cuponInput;
    }
    
    const result = await invokeApi("/agenda/pagoCrear", "post", apiData);
    
    if (result.error) {
      MySwal.fire(result.error.message || "Error del servidor", "", "error");
      return;
    }
    
    if (
      result &&
      result.message === "Preferencia MercadoPago creada" &&
      result.preference_id
    ) {
      setPreferenceId(result.preference_id);
      setPaymentId(result.payment_id);
      setApiCrear(result);
      handlePagarClick(result.payment_id, result.preference_id);
    } else {
      console.error("Respuesta inesperada:", result);
      MySwal.fire("Respuesta inesperada del servidor", "", "error");
    }
  } catch (error) {
    setIsLoading(false);
    console.error("Error al crear pago:", error);
    MySwal.fire("Error al procesar el pago", "", "error");
  }
};

  const createTransferencia = async () => {
    setIsLoading(true);

    try {
      const apiData = {
        id_profesional_fantasma: location.state.id_kinesiologo,
        id_servicio_fantasma: location.state.id_servicios,
        fecha_agenda: location.state.dates,
        hora_agenda: location.state.hourss,
        paciente_email: mail,
      };

      // Agregar SOLO código de cupón si fue validado (backend calcula el precio)
      if (cuponAplicado === true) {
        apiData.cupon_codigo = cuponInput;
      }

      const result = await invokeApi("/agenda/transferenciaCrear", "post", apiData);

      if (result.error) {
        setIsLoading(false);
        MySwal.fire(result.error.message || "Error del servidor", "", "error");
        return;
      }

      if (result && result.transferencia_id) {
        setPaymentId(result.transferencia_id);
        handlePagarClick(result.transferencia_id, null);
      } else {
        setIsLoading(false);
        console.error("Respuesta inesperada:", result);
        MySwal.fire("Respuesta inesperada del servidor", "", "error");
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error al crear transferencia:", error);
      MySwal.fire("Error al procesar la transferencia", "", "error");
    }
  };

  const handlePaymentSubmit = () => {
    if (paymentMethod === "transfer") {
      createTransferencia();
    } else if (paymentMethod === "webpay") {
      createWebpayPayment();
    } else {
      createPayment();
    }
  };

  const handlePagarClick = async (payment_id, preference_id, transbankDataParam = null) => {
    setIsLoading(true);

    try {
      const apiData = {
        id_profesional_fantasma: location.state.id_kinesiologo,
        id_servicio_fantasma: location.state.id_servicios,
        fecha_agenda: location.state.dates,
        hora_agenda: location.state.hourss,
        paciente_nombre: nombre,
        paciente_apellido: apellido,
        paciente_rut: rut,
        paciente_es_pasaporte: !formatoRut,
        paciente_email: mail,
        paciente_celular: `${telefono.countryCode}${telefono.number}`,
        paciente_direccion: isDomicilio ? (direccion || "") : null,
        paciente_motivo_consulta: text || "",
        paciente_direccion_oficina: isDomicilio ? (depto || "") : null,
        id_paciente: id_paciente,
        paciente_datos_nuevos:
          validationResult?.paciente === "paciente_registrado"
            ? isEditing
              ? 1
              : 0
            : 0,
      };

      if (cuponAplicado === true) {
        apiData.cupon_codigo = cuponInput;
      }

      if (location.state.perfil_pago && paymentMethod === "mercadopago") {
        apiData.preference_id = preference_id;
        apiData.payment_id = payment_id;
      } else if (location.state.perfil_pago && paymentMethod === "transfer") {
        apiData.transferencia_id = payment_id;
      } else if (location.state.perfil_pago && paymentMethod === "webpay") {
        apiData.transbank_session_id = transbankDataParam?.session_id || transbankData.session_id;
        apiData.transbank_token = transbankDataParam?.token || transbankData.token;
        apiData.transbank_buy_order = transbankDataParam?.buy_order || transbankData.buy_order;
      }

      const result = await invokeApi("/agenda/agendarIntencion", "post", apiData);

      if (result.error) {
        const isHorarioNoDisponible = result.error.code === 409;
        const platformUrl = result.error.serverData?.platform_url;
        
        MySwal.fire({
          title: isHorarioNoDisponible
            ? "Horario no disponible"
            : "No se pudo completar la reserva",
          text: isHorarioNoDisponible
            ? "Este horario ya ha sido reservado. Te invitamos a seleccionar otro horario disponible."
            : "No se pudo completar tu reserva. Por favor intenta más tarde.",
          icon: "info",
          confirmButtonText: "Entendido",
          confirmButtonColor: "#3B82F6",
        }).then(() => {
          if (platformUrl) {
            window.location.href = platformUrl;
          }
        });
        return;
      }

      if (result?.statusCode === 200) {
        if (esServicioGratuito || esReservaGratuitaPorCupon) {
          navigate("/pro/perfil/exito", {
            state: {
              id_intencion: result.id_intencion,
              metodo_pago: "gratuito",
            },
            replace: true,
          });
        } else if (
          location.state.perfil_pago &&
          paymentMethod === "mercadopago"
        ) {
          setShouldShowWallet(true);
          await waitForWalletReady();
          tryAutomaticPayment();
        } else if (location.state.perfil_pago && paymentMethod === "transfer") {
          navigate("/pro/transferencia/rut_pagador", {
            state: {
              id_intencion: result.id_intencion,
              transferencia_id: payment_id,
              metodo_pago: paymentMethod,
              monto: location.state.servicio_precios,
              servicio_nombre: location.state.servicio_nombre,
              profesional_nombre: location.state.titulo,
              fecha_agenda: location.state.fecha_formateada,
              hora_agenda: location.state.hourss,
              servicio_modalidad: location.state.servicio_modalidad,
            },
          });
        } else if (location.state.perfil_pago && paymentMethod === "webpay") {
          const transbankToUse = transbankDataParam || transbankData;
          redirectToTransbank(transbankToUse.token, transbankToUse.url);
        } else {
          navigate("/pro/perfil/exito", {
            state: {
              id_intencion: result.id_intencion,
              metodo_pago: paymentMethod,
            },
            replace: true,
          });
        }
      } else {
        MySwal.fire("Respuesta inesperada del servidor", "", "error");
      }
    } catch (error) {
      console.error("Error detallado al procesar la solicitud:", error);
      MySwal.fire("Error inesperado. Por favor intenta más tarde.", "", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Funciones de MercadoPago
  const waitForWalletReady = () => {
    return new Promise((resolve) => {
      const checkWallet = () => {
        if (walletRef.current && walletRef.current.querySelector("button")) {
          setIsWalletReady(true);
          resolve();
        } else {
          setTimeout(checkWallet, 100);
        }
      };
      checkWallet();
    });
  };

  const tryAutomaticPayment = () => {
    if (walletRef.current) {
      const mpButton = walletRef.current.querySelector("button");
      if (mpButton) {
        console.log("Intentando clic automático en el botón de MercadoPago");
        mpButton.click();
      } else {
        console.error("No se encontró el botón de MercadoPago");
        showManualPaymentMessage();
      }
    } else {
      console.error("No se encontró la referencia del wallet");
      showManualPaymentMessage();
    }
  };

  const redirectToTransbank = (token, url) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    
    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'token_ws';
    tokenInput.value = token;
    
    form.appendChild(tokenInput);
    document.body.appendChild(form);
    
    form.submit();
  };

  const showManualPaymentMessage = () => {
    MySwal.fire({
      title: "Pago Manual Requerido",
      text: "No se pudo iniciar el pago automáticamente. Por favor, haga clic en el botón de pago que aparece debajo.",
      icon: "info",
      confirmButtonText: "Entendido",
    });
  };

  return (
    <>
      
      <LoaderOverlay isLoading={isLoading} />
      
      <img
        src={logoImage}
        alt="Clinifica Logo"
        style={{
          width: isMobile ? "150px" : "200px",
          height: "auto",
          paddingTop: "15px",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
          padding: 15,
        }}
      />
      
      <Box
        translate="no"                    // ← AGREGAR
        className="notranslate"           // ← AGREGAR
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "#fbfafe",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 2,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        <Typography sx={{ 
          fontFamily: "Quicksand-Regular",
          color: "#626262",                    // ← Gris como B
          fontSize: { xs: "14px", md: "16px" }, // ← Orden como B
          mb: 0
        }}>
          Estas agendando con:
        </Typography>
        <Typography
          sx={{
            mt: -0.5,
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "18px", md: "20px" }, // ← Tamaños como B
            color: "#191C1F",
            mb: 0
          }}
        >
          {location.state.titulo}
        </Typography>
        <Typography
          sx={{ 
            fontFamily: "Quicksand-Regular", 
            mt: -0.5,
            fontSize: { xs: "14px", md: "16px" }, // ← Orden como B
            color: "default",                     // ← Sin color explícito como B
            mb: 0
          }}
        >
          {location.state.profesion}
        </Typography>
        <Typography sx={{ 
          fontFamily: "Quicksand-Regular",
          color: "#626262",                    // ← Gris como B
          mt: 2,
          fontSize: { xs: "14px", md: "16px" }, // ← Orden como B
          mb: 0
        }}>
          El servicio de:
        </Typography>
        <Typography
          sx={{
            mt: -0.5,
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "18px", md: "20px" }, // ← Tamaños como B
            color: "#191C1F",
            mb: 0
          }}
        >
          {location.state.servicio_nombre}
        </Typography>
        <Typography
          sx={{ 
            fontFamily: "Quicksand-Regular", 
            mt: -0.5,                    // ← Distancia nombre→precio: -0.5 ✅
            fontSize: { md: "16px", xs: "14px" },
            color: "#191C1F",
            mb: 0
          }}
        >
          {location.state.servicio_precios}
        </Typography>
        <Box
          sx={{
            display: "inline-block",
            backgroundColor: "#f0f4ff",
            color: "#3B82F6",
            fontFamily: "Quicksand-Medium",
            fontSize: { md: "15px", xs: "13px" },
            borderRadius: 2,
            paddingLeft: 0.75,
            paddingRight: 0.75,
            paddingTop: 0.25,
            paddingBottom: 0.25,
            marginLeft: -0.75,
            mt: -0.75,                   // ← Distancia precio→modalidad: -0.75 + 0.25 = -0.5 ✅
            textAlign: "left",
          }}
        >
          {transformModalidad(location.state.servicio_modalidad)}
        </Box>
        <Typography
          sx={{
            mt: 0,                   // ← Distancia modalidad→fecha: -0.75 + 0.25 = -0.5 ✅
            color: "#007FFF",
            fontFamily: "Quicksand-Bold",
            fontSize: { md: "16px", xs: "14px" },
          }}
        >
          {formatearFechaSinAnio(location.state.fecha_formateada)} {"-"}{" "}
          {location.state.hourss}
        </Typography>
      </Box>

      <Typography
        sx={{
          fontFamily: "Quicksand-Bold",
          fontSize: { md: "24px", xs: "20px" },
          textAlign: "center",
          color: "#191C1F",
          mt: 3,
          mb: 1,
        }}
      >
        Datos del Paciente
      </Typography>

      <Box
        translate="no"
        className="notranslate"
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "white",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 8,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        <FormGroup>
          <Container maxWidth={false} disableGutters>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", md: "66.67%" },
                  mx: "auto",
                }}
              >
                {/* --- SECCIÓN DE VALIDACIÓN (SIEMPRE VISIBLE) --- */}
                <Box sx={{ width: "100%", mb: 2 }}>
                  <RutValidationGrid
                    rut={rut}
                    handleChangeRut={handleChangeRut}
                    isRutValidated={isRutValidated}
                    formatoRut={formatoRut}
                    handleCheckboxChange={handleCheckboxChange}
                    validationResult={validationResult}
                  />
                </Box>

                {!isRutValidated && (
                  <Button
                    onClick={validateRut}
                    variant="contained"
                    fullWidth
                    disabled={
                      (formatoRut ? rut.length < 9 : rut.length < 1) ||
                      isValidating
                    }
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontFamily: "Quicksand-Bold",
                      backgroundColor: "#3B82F6",
                      "&:hover": { backgroundColor: "#2563EB" },
                      borderRadius: 2,
                      py: 1.5,
                      mb: 2,
                      textTransform: "none",
                      position: 'relative'
                    }}
                  >
                    {isValidating ? (
                      <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : (
                      "Validar"
                    )}
                  </Button>
                )}
                
                {/* --- SECCIÓN DATOS DEL PACIENTE (CONDICIONAL) --- */}
                {isRutValidated && (
                  <>
                    {/* Nombre */}
                    <Box sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            pt: 0,
                            fontFamily: isValidText(nombre)
                              ? "Quicksand-Bold"
                              : "Quicksand-Regular",
                            fontSize: { md: "16px", xs: "14px" },
                            color: isFieldDisabled()
                              ? "#9e9e9e"
                              : isValidText(nombre)
                              ? "#2669A8"
                              : "#191C1F",
                          }}
                        >
                          {isValidText(nombre) ? "Nombre" : "Nombre*"}
                        </Typography>
                        {validationResult?.paciente === "paciente_registrado" &&
                          validationResult?.datos_paciente && (
                            <Button
                              onClick={handleEditData}
                              sx={{
                                fontFamily: isEditing
                                  ? "Quicksand-Bold"
                                  : "Quicksand-Regular",
                                fontSize: { md: "14px", xs: "12px" },
                                color: isEditing ? "#3B82F6" : "#9e9e9e",
                                "&:hover": {
                                  backgroundColor: isEditing
                                    ? "rgba(59, 130, 246, 0.1)"
                                    : "rgba(158, 158, 158, 0.1)",
                                },
                                textTransform: "none",
                              }}
                            >
                              {isEditing
                                ? " ⬅️ Mantener datos guardados"
                                : "Editar datos"}
                            </Button>
                          )}
                      </Box>
                      <Box
                        sx={{
                          ...getFieldContainerStyle(),
                          minHeight: "22px",
                        }}
                      >
                        <TextField
                          value={nombre}
                          onChange={handleChangeNombre}
                          disabled={isFieldDisabled()}
                          inputProps={{ maxLength: 50 }}
                          variant="standard"
                          fullWidth
                          InputProps={{
                            disableUnderline: true,
                            sx: {
                              border: "none",
                              outline: "none",
                              background: "transparent",
                              fontFamily: "Quicksand-Regular",
                              color: "#4F4F4F",
                              fontSize: { md: "16px", xs: "14px" },
                              "& input": {
                                padding: 0,
                              },
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Apellido */}
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        sx={{
                          pt: 1,
                          fontFamily: isValidText(apellido)
                            ? "Quicksand-Bold"
                            : "Quicksand-Regular",
                          fontSize: { md: "16px", xs: "14px" },
                          color: isFieldDisabled()
                            ? "#9e9e9e"
                            : isValidText(apellido)
                            ? "#2669A8"
                            : "#191C1F",
                        }}
                      >
                        {isValidText(apellido) ? "Apellido" : "Apellido*"}
                      </Typography>
                      <Box
                        sx={{
                          ...getFieldContainerStyle(),
                          minHeight: "22px",
                        }}
                      >
                        <TextField
                          value={apellido}
                          onChange={handleChangeApellido}
                          disabled={isFieldDisabled()}
                          inputProps={{ maxLength: 50 }}
                          variant="standard"
                          fullWidth
                          InputProps={{
                            disableUnderline: true,
                            sx: {
                              border: "none",
                              outline: "none",
                              background: "transparent",
                              fontFamily: "Quicksand-Regular",
                              color: "#4F4F4F",
                              fontSize: { md: "16px", xs: "14px" },
                              "& input": {
                                padding: 0,
                              },
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Campo de Email */}
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        sx={{
                          pt: 1,
                          fontFamily: isValidEmail(mail, validationResult) // Agregado validationResult
                            ? "Quicksand-Bold"
                            : "Quicksand-Regular",
                          fontSize: { md: "16px", xs: "14px" },
                          color: isFieldDisabled()
                            ? "#9e9e9e"
                            : isValidEmail(mail, validationResult) // Agregado validationResult
                            ? "#2669A8"
                            : "#191C1F",
                        }}
                      >
                        {isValidEmail(mail, validationResult) ? "Email" : "Email*"} {/* Agregado validationResult */}
                      </Typography>
                      <Box sx={getFieldContainerStyle()}>
                        <TextField
                          type="email"
                          value={mail}
                          onChange={handleChangeMail}
                          disabled={isFieldDisabled()}
                          inputProps={{ maxLength: 50 }}
                          variant="standard"
                          fullWidth
                          InputProps={{
                            disableUnderline: true,
                            sx: {
                              border: "none",
                              outline: "none",
                              background: "transparent",
                              fontFamily: "Quicksand-Regular",
                              color: "#4F4F4F",
                              fontSize: { md: "16px", xs: "14px" },
                              "& input": {
                                padding: 0,
                              },
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Confirmar Email */}
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        sx={{
                          pt: 1,
                          fontFamily:
                            mail &&
                            confirmMail &&
                            areEmailsEqual(mail, confirmMail) &&
                            isValidEmail(mail, validationResult) // Agregado validación con contexto
                              ? "Quicksand-Bold"
                              : "Quicksand-Regular",
                          fontSize: { md: "16px", xs: "14px" },
                          color: isFieldDisabled()
                            ? "#9e9e9e"
                            : mail &&
                              confirmMail &&
                              areEmailsEqual(mail, confirmMail) &&
                              isValidEmail(mail, validationResult) // Agregado validación con contexto
                            ? "#2669A8"
                            : "#191C1F",
                        }}
                      >
                        {mail && confirmMail && areEmailsEqual(mail, confirmMail) && isValidEmail(mail, validationResult) // Agregado validación con contexto
                          ? "Confirmar Email"
                          : "Confirmar Email*"}
                      </Typography>
                      <Box sx={getFieldContainerStyle()}>
                        <TextField
                          type="email"
                          value={confirmMail}
                          onChange={handleChangeConfirmMail}
                          disabled={isFieldDisabled()}
                          onPaste={(e) => e.preventDefault()}
                          inputProps={{ maxLength: 50 }}
                          variant="standard"
                          fullWidth
                          InputProps={{
                            disableUnderline: true,
                            sx: {
                              border: "none",
                              outline: "none",
                              background: "transparent",
                              fontFamily: "Quicksand-Regular",
                              color: "#4F4F4F",
                              fontSize: { md: "16px", xs: "14px" },
                              "& input": {
                                padding: 0,
                              },
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Campo de Teléfono */}
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        sx={{
                          pt: 1,
                          fontFamily: isValidPhone(telefono)
                            ? "Quicksand-Bold"
                            : "Quicksand-Regular",
                          fontSize: { md: "16px", xs: "14px" },
                          color: isFieldDisabled()
                            ? "#9e9e9e"
                            : isValidPhone(telefono)
                            ? "#2669A8"
                            : "#191C1F",
                        }}
                      >
                        {isValidPhone(telefono) ? "Teléfono" : "Teléfono*"}
                      </Typography>
                      <Box sx={getFieldContainerStyle()}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {isMobile ? (
                            <select
                              value={telefono.countryCode}
                              onChange={handleChangeCountry}
                              disabled={isFieldDisabled()}
                              style={{
                                width: (() => {
                                  const codeLength =
                                    telefono.countryCode?.length || 0;
                                  if (codeLength <= 2) return "35px";
                                  if (codeLength === 3) return "45px";
                                  return "55px";
                                })(),
                                marginLeft: "4px",
                                marginRight: "4px",
                                fontFamily: "Quicksand-Regular",
                                fontSize: "14px",
                                color: "#4F4F4F",
                                padding: 0,
                                border: "none",
                                outline: "none",
                                background: "transparent",
                                cursor: isFieldDisabled()
                                  ? "not-allowed"
                                  : "pointer",
                                opacity: isFieldDisabled() ? 0.6 : 1,
                              }}
                            >
                              {COUNTRIES.map((country) => (
                                <option
                                  key={`${country.code}${'\u00A0'.repeat(6)}${country.name}`}
                                  value={country.code}
                                >
                                  {`${country.code}${'\u00A0'.repeat(6)}${country.name}`}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <Select
                              value={telefono.countryCode}
                              onChange={handleChangeCountry}
                              renderValue={(value) => value}
                              IconComponent={KeyboardArrowDown}
                              disabled={isFieldDisabled()}
                              sx={{
                                width: "72.5px",
                                marginLeft: "4px",
                                marginRight: "4px",
                                "& .MuiSelect-select": {
                                  fontFamily: "Quicksand-Regular",
                                  fontSize: { md: "16px", xs: "14px" },
                                  color: "#4F4F4F",
                                  padding: 0,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "& .MuiSelect-icon": {
                                  color: "#4F4F4F",
                                },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: { maxHeight: 300 },
                                },
                              }}
                            >
                              {COUNTRIES.map((country) => (
                                <MenuItem
                                  key={`${country.code}-${country.name}`}
                                  value={country.code}
                                  sx={{
                                    fontFamily: "Quicksand-Regular",
                                    fontSize: { md: "14px", xs: "12px" },
                                  }}
                                >
                                  <span style={{ marginLeft: "4px" }}>
                                  {`${country.code}      ${country.name}`}
                                  </span>
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                          <TextField
                            value={telefono.number}
                            onChange={handleChangeTelefono}
                            disabled={isFieldDisabled()}
                            variant="standard"
                            fullWidth
                            InputProps={{
                              disableUnderline: true,
                              sx: {
                                border: "none",
                                flex: 1,
                                outline: "none",
                                background: "transparent",
                                borderLeft: "2px solid #d0d0d0",
                                paddingLeft: "6px",
                                fontFamily: "Quicksand-Regular",
                                color: "#4F4F4F",
                                fontSize: { md: "16px", xs: "14px" },
                                "& input": {
                                  padding: 0,
                                },
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Dirección y Depto (SOLO cuando el servicio es a domicilio) */}
                    {isDomicilio && (
                      <>
                        {/* Dirección */}
                        <Box sx={{ width: "100%" }}>
                          {isGoogleMapsLoaded ? (
                            <AddressInput
                              onAddressChange={setDireccion}
                              onComunaChange={handleComunaChange}
                              disabled={isAddressDisabled()}
                              direccion={direccion}
                            />
                          ) : (
                            <Typography sx={{ color: "#9e9e9e", p: 1 }}>
                              Cargando direcciones...
                            </Typography>
                          )}
                        </Box>

                        {/* Departamento */}
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            sx={{
                              pt: 1,
                              fontFamily: "Quicksand-Regular",
                              fontSize: { md: "16px", xs: "14px" },
                              color: isAddressDisabled() ? "#9e9e9e" : "#191C1F",
                            }}
                          >
                            Depto, oficina u otro - opcional
                          </Typography>
                          <Box sx={getFieldContainerStyle(isAddressDisabled())}>
                            <TextField
                              value={depto}
                              onChange={handleChangeDepto}
                              disabled={isAddressDisabled()}
                              inputProps={{ maxLength: 50 }}
                              variant="standard"
                              fullWidth
                              InputProps={{
                                disableUnderline: true,
                                sx: {
                                  border: "none",
                                  outline: "none",
                                  background: "transparent",
                                  fontFamily: "Quicksand-Regular",
                                  color: "#4F4F4F",
                                  fontSize: { md: "16px", xs: "14px" },
                                  "& input": {
                                    padding: 0,
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </>
                    )}

                    {/* Motivo de consulta */}
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        sx={{
                          pt: 1,
                          fontFamily: "Quicksand-Regular",
                          fontSize: { md: "16px", xs: "14px" },
                          color: !isRutValidated ? "#9e9e9e" : "#191C1F",
                        }}
                      >
                        Motivo de consulta - opcional
                      </Typography>
                      <Box
                        sx={{
                          borderRadius: 2,
                          border: "1px solid #909090",
                          backgroundColor: !isRutValidated
                            ? "#f5f5f5"
                            : "white",
                          opacity: !isRutValidated ? 0.6 : 1,
                          p: 0.5,
                          "&:focus-within": {
                            borderColor: "#3B82F6",
                            boxShadow: "0 0 0 1px #3B82F6",
                          },
                        }}
                      >
                        <TextareaAutosize
                          onChange={handleChangeDiagnostico}
                          value={text}
                          maxLength={250}
                          disabled={!isRutValidated}
                          style={{
                            border: "none",
                            width: "96%",
                            outline: "none",
                            background: "transparent",
                            fontFamily: "Quicksand-Regular",
                            color: "#4F4F4F",
                            fontSize: "16px",
                            resize: "none",
                            height: "120px",
                            cursor: !isRutValidated ? "not-allowed" : "text",
                          }}
                        />
                      </Box>
                    </Box>

                    <Typography
                      sx={{
                        textAlign: "right",
                        mb: 2,
                        pt: 1,
                        fontFamily: "Quicksand-Regular",
                        fontSize: { md: "16px", xs: "14px" },
                        color: "#191C1F",
                      }}
                    >
                      * Obligatorio
                    </Typography>

                    {/* Condiciones de servicio */}
                    {location.state.servicio_condiciones === 1 && (
                      <Box sx={{ width: "100%" }}>
                        <Typography
                          sx={{
                            fontFamily: "Quicksand-Regular",
                            fontSize: { md: "16px", xs: "14px" },
                            color: "#191C1F",
                            mb: 1,
                          }}
                        >
                          Condiciones de servicio
                        </Typography>
                        <Box
                          sx={{
                            borderRadius: 2,
                            border: "1px solid #e0e0e0",
                            p: 1.5,
                            mb: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Quicksand-Regular",
                              fontSize: { md: "14px", xs: "12px" },
                              whiteSpace: "pre-line",
                              color: "#191C1F",
                            }}
                          >
                            {location.state.servicio_condiciones_descripcion}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Área de pago */}
                    <Box
                      ref={paymentFormRef}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        mt: { xs: 3, lg: 2 },
                        mb: { xs: 3, lg: 2 },
                        width: "100%",
                        position: "relative",
                      }}
                      tabIndex={-1}
                    >
                      {/* ... (el contenido del área de pago permanece igual) ... */}
                       {!comunaCobertura &&
                    comuna &&
                    location.state.servicio_modalidad === "domicilio" && (
                      <Box
                        sx={{
                          backgroundColor: "#ffebee",
                          border: "2px solid #f44336",
                          borderRadius: 2,
                          marginBottom: 2,
                          width: "100%",
                          maxWidth: "100%",
                          boxSizing: "border-box",
                          p: 2,
                          overflow: "hidden",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Quicksand-Bold",
                            fontSize: { md: "16px", xs: "14px" },
                            color: "#d32f2f",
                            textAlign: "center",
                            marginBottom: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            hyphens: "auto",
                          }}
                        >
                          Comuna sin cobertura para atención domiciliaria
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Quicksand-Regular",
                            fontSize: { md: "14px", xs: "12px" },
                            textAlign: "center",
                            marginBottom: 1,
                            color: "#191C1F",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          El profesional no atiende a domicilio en {comuna}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Quicksand-Regular",
                            fontSize: { md: "14px", xs: "12px" },
                            color: "#666",
                            textAlign: "center",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            lineHeight: 1.4,
                          }}
                        >
                          Comunas disponibles: {comunasAtendidas.join(", ")}
                        </Typography>
                      </Box>
                    )}

                  {/* Selector de método de pago */}
                  {location.state.perfil_pago && !esServicioGratuito && !esReservaGratuitaPorCupon && (
                    <PaymentMethodBox
                      onPaymentMethodChange={handlePaymentMethodChange}
                      tarjeta={location.state.perfil_pago_tarjeta}
                      transferencia={location.state.perfil_pago_transferencia}
                      selectedMethod={paymentMethod}
                    />
                  )}

                  {/* Campo de cupón de descuento */}
                  {location.state.servicio_cupones_activos === 1 && isRutValidated && (
                    <Box sx={{ width: "100%", mb: 2 }}>
                      <Typography
                        sx={{
                          fontFamily: cuponInput.trim() !== "" ? "Quicksand-Bold" : "Quicksand-Regular",
                          fontSize: { md: "16px", xs: "14px" },
                          color: (cuponValidandose || tipoMensajeCupon === "ok")
                            ? "#9e9e9e" 
                            : (cuponInput.trim() !== "" ? "#2669A8" : "#191C1F"),
                          mb: 0.5,
                        }}
                      >
                        {cuponInput.trim() !== "" ? "Código de convenio" : "Código de convenio"}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            width: "75%",
                            borderRadius: 2,
                            border: "1px solid #e0e0e0",
                            backgroundColor: (cuponValidandose || tipoMensajeCupon === "ok") ? "#f5f5f5" : "white",
                            opacity: (cuponValidandose || tipoMensajeCupon === "ok") ? 0.6 : 1,
                            display: "flex",
                            alignItems: "center",
                            height: "38px",
                            "&:focus-within": {
                              borderColor: (cuponValidandose || tipoMensajeCupon === "ok") ? "#e0e0e0" : "#3B82F6",
                              boxShadow: (cuponValidandose || tipoMensajeCupon === "ok") ? "none" : "0 0 0 1px #3B82F6",
                            },
                          }}
                        >
                          <TextField
                            value={cuponInput}
                            onChange={handleCuponInputChange}
                            disabled={cuponValidandose || tipoMensajeCupon === "ok"}
                            placeholder="Ingrese código"
                            inputProps={{ maxLength: 20 }}
                            variant="standard"
                            fullWidth
                            InputProps={{
                              disableUnderline: true,
                              sx: {
                                border: "none",
                                outline: "none",
                                background: "transparent",
                                fontFamily: "Quicksand-Regular",
                                color: "#4F4F4F",
                                fontSize: { md: "16px", xs: "14px" },
                                paddingLeft: "8px",
                                paddingRight: "8px",
                                height: "100%",
                                cursor: (cuponValidandose || tipoMensajeCupon === "ok") ? "not-allowed" : "text",
                                "& input": {
                                  padding: "0",
                                  height: "100%",
                                },
                              },
                            }}
                          />
                        </Box>
                        <Button
                          onClick={handleAplicarCupon}
                          disabled={cuponValidandose || tipoMensajeCupon === "ok"}
                          disableRipple
                          disableElevation
                          disableFocusRipple
                          sx={{
                            width: "25%",
                            fontFamily: "Quicksand-Bold",
                            fontSize: { md: "14px", xs: "12px" },
                            backgroundColor: 
                              tipoMensajeCupon === "ok" 
                                ? "#3B82F6" 
                                : (tipoMensajeCupon === "invalido" || tipoMensajeCupon === "agotado")
                                  ? "#EF4444" 
                                  : (cuponInput.trim() === "" ? "#99befa" : "#3B82F6"),
                            color: "white",
                            "&.Mui-disabled": {
                              color: "white",
                            },
                            borderRadius: 2,
                            height: "38px",
                            boxShadow: "none !important",
                            transition: "none !important",
                            textTransform: "none",
                            "&:hover": {
                              backgroundColor: 
                                tipoMensajeCupon === "ok" 
                                  ? "#3B82F6" 
                                  : (tipoMensajeCupon === "invalido" || tipoMensajeCupon === "agotado")
                                    ? "#EF4444" 
                                    : (cuponInput.trim() === "" ? "#99befa" : "#3B82F6"),
                              boxShadow: "none !important",
                            },
                            "&:active": {
                              backgroundColor: 
                                tipoMensajeCupon === "ok" 
                                  ? "#3B82F6" 
                                  : (tipoMensajeCupon === "invalido" || tipoMensajeCupon === "agotado")
                                    ? "#EF4444" 
                                    : (cuponInput.trim() === "" ? "#99befa" : "#3B82F6"),
                              boxShadow: "none !important",
                            },
                            "&:focus": {
                              backgroundColor: 
                                tipoMensajeCupon === "ok" 
                                  ? "#3B82F6" 
                                  : (tipoMensajeCupon === "invalido" || tipoMensajeCupon === "agotado")
                                    ? "#EF4444" 
                                    : (cuponInput.trim() === "" ? "#99befa" : "#3B82F6"),
                              boxShadow: "none !important",
                            },
                            "& .MuiTouchRipple-root": {
                              display: "none",
                            },
                          }}
                        >
                          {cuponValidandose ? (
                            <CircularProgress size={20} sx={{ color: "white" }} />
                          ) : tipoMensajeCupon === "ok" ? (
                            "OK"
                          ) : tipoMensajeCupon === "invalido" ? (
                            "INVÁLIDO"
                          ) : tipoMensajeCupon === "agotado" ? (
                            "AGOTADO"
                          ) : (
                            "Aplicar"
                          )}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* Caja informativa de cupón personalizado */}
                  {location.state.servicio_cupones_activos === 1 && 
                   isRutValidated && 
                   cuponAutomatico && 
                   cuponAplicado && 
                   tipoMensajeCupon === "ok" && 
                   validationResult?.datos_paciente?.nombres && 
                   validationResult?.datos_paciente?.apellidos && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                        paddingTop: 1,
                        paddingBottom: 1,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        width: "100%",
                        backgroundColor: "transparent",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", ml: 2, mr: 2 }}>
                        <Typography
                          sx={{
                            fontFamily: "Quicksand-Regular",
                            fontSize: { md: "16px", xs: "14px" },
                            color: "#191C1F",
                          }}
                        >
                          <span style={{ fontWeight: "bold" }}>{location.state.titulo}</span> creó un código de descuento para este servicio, que es de uso exclusivo para ti, <span style={{ fontWeight: "bold" }}>{validationResult.datos_paciente.nombres} {validationResult.datos_paciente.apellidos}</span>
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Desglose de precios */}
                  {location.state.servicio_cupones_activos === 1 && isRutValidated && (
                    <Box sx={{ width: "100%", mb: 2 }}>
                      {!cuponAplicado ? (
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography
                            sx={{
                              fontFamily: "Quicksand-Bold",
                              fontSize: { md: "18px", xs: "16px" },
                              color: "#191C1F",
                            }}
                          >
                            Total a pagar
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Quicksand-Bold",
                              fontSize: { md: "18px", xs: "16px" },
                              color: "#191C1F",
                            }}
                          >
                            ${precioOriginal.toLocaleString('es-CL')}
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                            <Typography
                              sx={{
                                fontFamily: "Quicksand-Regular",
                                fontSize: { md: "16px", xs: "14px" },
                                color: "#191C1F",
                              }}
                            >
                              Subtotal
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Quicksand-Bold",
                                fontSize: { md: "16px", xs: "14px" },
                                color: "#191C1F",
                              }}
                            >
                              ${precioOriginal.toLocaleString('es-CL')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                            <Typography
                              sx={{
                                fontFamily: "Quicksand-Regular",
                                fontSize: { md: "16px", xs: "14px" },
                                color: "#191C1F",
                              }}
                            >
                              Descuento
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Quicksand-Bold",
                                fontSize: { md: "16px", xs: "14px" },
                                color: "#3B82F6",
                              }}
                            >
                              -${descuentoActual.toLocaleString('es-CL')}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              borderTop: "1px solid #d0d0d0",
                              my: 1,
                            }}
                          />
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography
                              sx={{
                                fontFamily: "Quicksand-Bold",
                                fontSize: { md: "18px", xs: "16px" },
                                color: "#191C1F",
                              }}
                            >
                              Total a pagar
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Quicksand-Bold",
                                fontSize: { md: "18px", xs: "16px" },
                                color: "#191C1F",
                              }}
                            >
                              ${precioFinal.toLocaleString('es-CL')}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Alerta consolidada amarilla */}
                  {getValidationAlert(rut, formatoRut, isRutValidated, nombre, apellido, mail, confirmMail, telefono, direccion, comunaCobertura, validationResult, isDomicilio) && (
                    <Box
                      sx={{
                        backgroundColor: "#FFF9E6",
                        padding: 1,
                        borderRadius: 2,
                        mb: 2,
                        textAlign: "center",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Quicksand-Regular",
                          fontSize: { md: "14px", xs: "12px" },
                          color: "#836516",
                        }}
                      >
                        {getValidationAlert(rut, formatoRut, isRutValidated, nombre, apellido, mail, confirmMail, telefono, direccion, comunaCobertura, validationResult, isDomicilio)}
                      </Typography>
                    </Box>
                  )}

{/* Botón principal */}
                  {esServicioGratuito ? (
                    <Button
                      sx={{
                        fontSize: { xs: "14px", md: "16px", lg: "16px" },
                        fontFamily: "Quicksand-Bold",
                        backgroundColor: "#3B82F6",
                        "&:hover": { backgroundColor: "#2563EB" },
                        borderRadius: 2,
                        zIndex: 2,
                        width: "100%",
                        paddingTop: 2,
                        paddingBottom: 2,
                        mb: 3,
                      }}
                      variant="contained"
                      disabled={!isFormValid || isLoading}
                      onClick={() => handlePagarClick()}
                    >
                      {isLoading ? (
                        <>
                          <CircularProgress 
                            size={20} 
                            sx={{ 
                              color: "white", 
                              mr: 1 
                            }} 
                          />
                        </>
                      ) : (
                        "Agendar"
                      )}
                    </Button>
                  ) : esReservaGratuitaPorCupon ? (
                    <Button
                      sx={{
                        fontSize: { xs: "14px", md: "16px", lg: "16px" },
                        fontFamily: "Quicksand-Bold",
                        backgroundColor: "#3B82F6",
                        "&:hover": { backgroundColor: "#2563EB" },
                        borderRadius: 2,
                        zIndex: 2,
                        width: "100%",
                        paddingTop: 2,
                        paddingBottom: 2,
                        mb: 3,
                      }}
                      variant="contained"
                      disabled={!isFormValid || isLoading}
                      onClick={() => handlePagarClick(null, null)}
                    >
                      {isLoading ? (
                        <>
                          <CircularProgress 
                            size={20} 
                            sx={{ 
                              color: "white", 
                              mr: 1 
                            }} 
                          />
                        </>
                      ) : (
                        "Agendar gratis"
                      )}
                    </Button>
                  ) : location.state.perfil_pago ? (
                    <Button
                      sx={{
                        fontSize: { xs: "14px", md: "16px", lg: "16px" },
                        fontFamily: "Quicksand-Bold",
                        backgroundColor: "#3B82F6",
                        "&:hover": { backgroundColor: "#2563EB" },
                        borderRadius: 2,
                        zIndex: 2,
                        width: "100%",
                        paddingTop: 2,
                        paddingBottom: 2,
                        mb: 3,
                      }}
                      variant="contained"
                      disabled={
                        !isFormValid ||
                        (paymentMethod === "mercadopago" && isWalletReady) ||
                        isLoading
                      }
                      onClick={handlePaymentSubmit}
                    >
                      {isLoading ? (
                        <>
                          <CircularProgress 
                            size={20} 
                            sx={{ 
                              color: "white", 
                              mr: 1 
                            }} 
                          />
                        </>
                      ) : (
                        `Pagar $${precioFinal.toLocaleString('es-CL')}`
                      )}
                    </Button>
                  ) : (
                    <Button
                      sx={{
                        fontSize: { xs: "14px", md: "16px", lg: "16px" },
                        fontFamily: "Quicksand-Bold",
                        backgroundColor: "#3B82F6",
                        "&:hover": { backgroundColor: "#2563EB" },
                        borderRadius: 2,
                        zIndex: 2,
                        width: "100%",
                        paddingTop: 2,
                        paddingBottom: 2,
                        mb: 3,
                      }}
                      variant="contained"
                      disabled={!isFormValid || isLoading}
                      onClick={() => handlePagarClick()}
                    >
                      {isLoading ? (
                        <>
                          <CircularProgress 
                            size={20} 
                            sx={{ 
                              color: "white", 
                              mr: 1 
                            }} 
                          />
                        </>
                      ) : (
                        "Agendar"
                      )}
                    </Button>
                  )}

                  {/* Widget de MercadoPago */}
                  {shouldShowWallet && paymentMethod === "mercadopago" && (
                    <div
                      ref={walletRef}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: isWalletReady ? 3 : 1,
                      }}
                    >
                      {<LoaderOverlay isLoading={true} />}
                      <Wallet
                        initialization={{ preferenceId: preferenceId }}
                        customization={{
                          texts: { action: "pay", valueProp: "security_safety" },
                          visual: {
                            borderRadius: "25px",
                            buttonBackground: "blue",
                            hideValueProp: true,
                          },
                        }}
                      />
                    </div>
                  )}
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          </Container>
        </FormGroup>

        {/* Botón volver */}
        <Button
          onClick={handleGoBack}
          sx={{
            fontFamily: "Quicksand-Bold",
            fontSize: { md: "16px", xs: "14px" },
            color: "#007FFF",
            "&:hover": { backgroundColor: "rgba(59, 130, 246, 0.1)" },
            borderRadius: 3,
            textTransform: "none",
            mt: 2, // Añadir margen superior para separar
          }}
        >
          Volver
        </Button>
      </Box>
    </>
  );
}
